import { FC, PropsWithChildren, useCallback, useMemo } from 'react';

import { FavoriteSwing } from '@data';
import { colors, ComparisonIcon, Text } from '@ui';

import * as css from './SwingCard.css';

export type SwingCardProps = Pick<FavoriteSwing, 'uuid'> &
    Partial<Pick<FavoriteSwing, 'createdAt' | 'clubName' | 'tags'>> & {
        title: string;
        number?: number;
        isSelected?: boolean;
        onClick: (uuid: string) => void;
        disabled?: boolean;
        fullWidth?: boolean;
        info?: string;
    };

type SwingCardBaseProps = {
    isSelected?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    fullWidth?: boolean;
};

export const SwingCardBase: FC<PropsWithChildren<SwingCardBaseProps>> = ({
    children,
    disabled,
    isSelected = false,
    onClick,
    fullWidth,
}) => (
    <button disabled={disabled} className={css.swingCard({ isSelected, disabled, fullWidth })} onClick={onClick}>
        {children}
    </button>
);

type SwingCardHeaderProps = {
    number?: number;
    isSelected?: boolean;
    info?: string;
};

export const SwingCardHeader: FC<PropsWithChildren<SwingCardHeaderProps>> = ({
    number,
    isSelected,
    info,
    children,
}) => {
    return (
        <div className={css.swingCardTop}>
            <div className={css.swingCardTopLeft}>
                {number && <div className={css.swingCardNumber({ isSelected })}>{number}</div>}
                <div>
                    {info && <Text variant="h4">{info}</Text>}
                    <Text variant="h3">{children}</Text>
                </div>
            </div>
            <ComparisonIcon color={isSelected ? colors.orange[500] : colors.bluegray[500]} />
        </div>
    );
};

type SwingCardTagsProps = {
    tags?: string[];
};

export const SwingCardTags: FC<SwingCardTagsProps> = ({ tags }: SwingCardTagsProps) => {
    if (!tags?.length) {
        return null;
    }
    return (
        <div className={css.swingCardTags}>
            {tags?.map((tag) => (
                <div key={tag} className={css.swingCardTag}>
                    {tag}
                </div>
            ))}
        </div>
    );
};

export const SwingCard: FC<SwingCardProps> = ({
    title,
    number,
    createdAt,
    clubName,
    tags,
    isSelected,
    onClick,
    disabled,
    uuid,
    fullWidth,
    info,
}) => {
    const handleSelect = useCallback(() => onClick(uuid), [onClick, uuid]);
    const allTags = useMemo((): string[] => {
        return [...(clubName ? [clubName] : []), ...(tags || [])];
    }, [clubName, tags]);

    const dateInfo = createdAt && new Date(createdAt).toLocaleDateString('gb-GB');

    return (
        <SwingCardBase disabled={disabled} isSelected={isSelected} onClick={handleSelect} fullWidth={fullWidth}>
            <SwingCardHeader isSelected={isSelected} number={number} info={info || dateInfo}>
                {title}
            </SwingCardHeader>
            <SwingCardTags tags={allTags} />
        </SwingCardBase>
    );
};

export const EmptySwingCard = () => {
    return (
        <SwingCardBase disabled={true}>
            <SwingCardHeader>{'-'}</SwingCardHeader>
        </SwingCardBase>
    );
};
